import type {
  ContractLineInvoiceLine,
  InvoiceLineContract,
  TaskProjectContract,
  UnbillableHour,
} from '../calc/invoicing.js';
import type { ServiceAgreementTerms } from '../calc/service-agreement.js';
import type { ContractBufferPlan } from '../calc/contract-buffer.js';
import type { ApplicationOperationsLine } from '../calc/application-operations.js';
import type { InvoicePeriod, InvoiceSchedule } from '../calc/invoice-schedule.js';
import type { CustomerInvoiceRules, RetainerInvoiceLine } from '../calc/invoice-rules.js';
import type { InvoiceBasis, Severity, UserRole } from './enums.js';
import type { Permission, RoleDefinition } from '../auth/permissions.js';
import type { ReportKey } from '../reports/report.js';
import type { IntegrationSettingName } from '../settings/integration-settings.js';
import type { ProjectStatus } from '../calc/project-status.js';
import type { BudgetSheetRow, BudgetSource } from '../calc/budget.js';

/**
 * API-kontrakten mellem web og api. `code` er engelsk og maskinlaesbar, `message` er dansk
 * og kan vises direkte i UI. PRD §14.12.
 */

export const ERROR_CODES = [
  'VALIDATION_ERROR',
  'UNAUTHORIZED',
  /**
   * Egen kode frem for FORBIDDEN, selvom statussen er den samme.
   *
   * Klienten skal kunne skelne "din rolle raekker ikke til *denne* handling" fra "din
   * konto er lukket". Den foerste henter profilen igen og retter menuen til; den anden
   * rydder sessionen og sender brugeren til login. At skelne paa den danske tekst ville
   * knytte en sikkerhedsbeslutning til en formulering.
   */
  'ACCOUNT_DEACTIVATED',
  'FORBIDDEN',
  'NOT_FOUND',
  'CONFLICT',
  'UNPROCESSABLE',
  'INTERNAL_ERROR',
] as const;
export type ErrorCode = (typeof ERROR_CODES)[number];

export interface ApiErrorBody {
  error: {
    code: ErrorCode;
    message: string;
    details?: Record<string, unknown>;
  };
  request_id: string;
}

export interface Paginated<T> {
  data: T[];
  total: number;
  limit: number;
  offset: number;
}

export interface AuthenticatedUser {
  /** public.users.id - den profilen og alle FK'er peger paa. */
  id: string;
  /**
   * public.users.auth_user_id - kontoen i auth.users, hvor adgangskoden ligger.
   *
   * De to id'er er ikke laengere det samme, og forskellen er ikke kosmetisk: opslag i
   * profil- og domaenetabeller skal bruge `id`, mens alt der gaar gennem Supabase Auth
   * (skift adgangskode, nulstil) skal bruge `auth_user_id`. Byttes de om, fejler kaldet
   * ikke - det rammer bare den forkerte konto, eller ingen.
   */
  auth_user_id: string;
  email: string;
  name: string;
  /** Den mest vidtgaaende af brugerens roller - den, der staar ved navnet. */
  role: UserRole;
  /** Rollens navn - a created role's own, see `RoleDefinition`. */
  role_label: string;
  /**
   * Det, brugeren maa - alle rollernes rettigheder, som de staar i databasen. Det er dem, der
   * spoerges paa, ikke `role`: rollerne rettes paa /admin/roller. Se `can`.
   */
  permissions: Permission[];
  /** Rapporterne, brugeren kan se. Se `canSeeReport`. */
  reports: ReportKey[];
  is_active: boolean;
  avatar_url: string | null;
}

/** En rolle paa /admin/roller - `GET /roles`. */
export interface RoleAccessRow {
  role: UserRole;
  /** Dansk. Over kolonnen - a created role's own name. */
  label: string;
  /** One of the four built-in roles: cannot be renamed or deleted. */
  built_in: boolean;
  /** Admin: har altid alt og kan ikke rettes. Se `LOCKED_ROLES`. */
  locked: boolean;
  /**
   * Om rollen har en raekke i `codepass_roles`. Uden den staar den paa standarden - se
   * `DEFAULT_ROLE_ACCESS` - og det goer den, til den gemmes foerste gang.
   */
  configured: boolean;
  permissions: Permission[];
  reports: ReportKey[];
  updated_at: string | null;
  updated_by_name: string | null;
  /** Antal brugere med rollen. */
  user_count: number;
}

export interface RolesResponse {
  /** False, saa laenge tabellerne fra docs/roller-opsaetning.md ikke findes. Saa kan intet gemmes. */
  ready: boolean;
  /**
   * False until the SQL for created roles in docs/roller-opsaetning.md has run - then no role can
   * be created, renamed or deleted, but the built-in ones work as before.
   */
  custom_roles_ready: boolean;
  roles: RoleAccessRow[];
}

/** `POST /roles` - a new role, with no rights. `PATCH /roles/:role` renames one. */
export interface SaveRoleRequest {
  label: string;
}

/** `GET /roles/options` - the roles a user can be given, for /admin/brugere. */
export interface RoleOptionsResponse {
  roles: RoleDefinition[];
}

/** `PUT /roles/:role`. Hele rollens saet - det, der ikke er med, fjernes. */
export interface UpdateRoleAccessRequest {
  permissions: Permission[];
  reports: ReportKey[];
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  user: AuthenticatedUser;
  /** True naar brugeren aldrig har skiftet password. Tvinger password-skift. */
  must_change_password: boolean;
}

/** Request til POST /invoices/preview. Muterer intet. */
export interface InvoicePreviewRequest {
  /**
   * Kunden i Business Central. **Grundlagets udgangspunkt.**
   *
   * Kunden vaelges foerst, og aftalerne vaelges derefter blandt kundens egne - se
   * `contract_ids`. Én faktura har altid præcis én modtager, ogsaa naar den daekker flere
   * aftaler.
   */
  customer_id?: string;
  /**
   * Aftalerne der skal faktureres, som kontrakt-id'er. Nul, én eller flere.
   *
   * Er der **flere**, kan grundlaget kun vaere `hours`: timer er det, der loeber op pr. sprint
   * paa hver sin aftale, mens et beloeb, en milepael og et abonnement er andele af én bestemt
   * kontraktsum. Er der præcis én, er alle grundlag mulige.
   *
   * Er listen tom, skal `without_contract` vaere sat - ellers er der ingenting at fakturere.
   */
  contract_ids?: string[];
  /**
   * Tag ogsaa kundens timer med, som **ikke** hoerer til nogen aftale.
   *
   * De staar som deres egen gruppe paa fakturaen, sidestillet med aftalerne. En tavle i OnePro
   * uden et kontraktnummer i Orchestrator er arbejde uden aftale - se demo/contract-labels.ts.
   */
  without_contract?: boolean;
  /**
   * Den enkelte aftale, naar faktureringen gaar gennem Codepass' eget skema.
   *
   * Kun `routes/invoices.ts` -> `buildDraft` bruger den. Den vej har loft, restbeloeb,
   * milepaele og snapshot bundet til én kontrakt og kender ikke flere aftaler pr. faktura;
   * live-vejen bruger `contract_ids`.
   */
  contract_id?: string;
  invoice_basis: InvoiceBasis;
  period_start?: string;
  period_end?: string;
  milestone_id?: string;
  amount_excl_vat?: number;
  /**
   * Timer, controlleren har sat i haanden pr. opgave - opgavens id til antal timer.
   *
   * Vejen, naar den samlede buffer ikke passer paa den enkelte opgave, fx fordi den bringer
   * fakturaen over opgavens estimat. Anvendes **efter** bufferen er fordelt og vinder over den
   * paa de opgaver, der staar her - se `applyTaskHours`.
   *
   * Med paa previewet og ikke kun paa oprettelsen: tallene skal kunne ses, foer fakturaen
   * laves, og previewet er det autoritative regnestykke.
   */
  task_hours?: Record<string, number>;
}

/** Én aftale paa fakturaen - blokken "Kontraktoplysninger" over dens linjer. */
export interface InvoicePreviewContract {
  id: string;
  contract_no: string | null;
  title: string;
  contract_sum_excl_vat: number;
  billed_excl_vat: number;
  remaining_excl_vat: number;
  /**
   * Bufferen der kan gives vaek af, som den vil staa **efter** denne faktura: det, aftalens
   * fakturaer har opsamlet - denne med - minus det, der tidligere er givet vaek. Foer noget gratis
   * arbejde paa *denne* faktura er trukket fra.
   *
   * Loftet for, hvor meget der kan gives vaek paa aftalen. /fakturering/ny bygger sin
   * `bufferRoom` af den og skjuler 'Gratis', naar der ikke er plads; serveren regner det samme
   * og afviser fakturaen. 0 betyder 'Ingen buffer' og dermed intet at give af.
   */
  buffer_excl_vat: number;
  /** Aftalens samlede buffer - maalet i `codepass_contract_buffer`. 0 naar ingen er sat. */
  buffer_target_excl_vat: number;
  /** Det, aftalens tidligere fakturaer har lagt paa - se `contractBufferLedger`. */
  buffer_collected_excl_vat: number;
  /**
   * Vilkaarene, naar aftalen er en serviceaftale - ellers null.
   *
   * /fakturering/ny bruger dem til at vise timer pr. opgave ved hver opgave, og til at sige, at
   * bufferen ikke kan laegges paa aftalen. Regnestykket er serverens - se
   * `groupHoursForServiceAgreement`.
   */
  service_agreement?: ServiceAgreementTerms | null;
}

export interface InvoicePreviewResponse {
  /** Fakturaens modtager. Én kunde pr. faktura, ogsaa naar den daekker flere aftaler. */
  customer: {
    name: string;
    currency: string;
  };
  /**
   * Aftalerne fakturaen daekker, i visningsraekkefoelge.
   *
   * Flere er det normale nu: timer paa to aftaler til den samme kunde hoerer paa den samme
   * faktura, grupperet pr. aftale. Listen er **tom**, naar der kun faktureres timer uden aftale
   * - gruppen findes da alene i `lines`, hvor `contract` er null.
   */
  contracts: InvoicePreviewContract[];
  /**
   * Én linje pr. kontraktlinje, med de opgaver den daekker i `tasks`.
   *
   * Timegrundlaget grupperes pr. kontraktlinje - kunden ser fasen fra aftalen, ikke de opgaver
   * den blev delt op i internt. `tasks` er bilagets niveau og staar ikke paa fakturaen.
   */
  lines: ContractLineInvoiceLine[];
  totals: {
    amount_excl_vat: number;
    vat_amount: number;
    amount_incl_vat: number;
    vat_rate: number;
    reverse_charge: boolean;
  };
  /**
   * Bufferen paa linjerne - intern, se `planInvoiceBuffer`.
   *
   * `mode` er 'auto', naar den er regnet af aftalernes samlede buffer, og 'manual', naar et
   * beloeb blev skrevet i feltet. `requested` er det skrevne beloeb (0 i auto). `applied` er det,
   * linjerne faktisk baerer ud over de brugte timer - ogsaa timer rettet i haanden - og det er
   * det, der staar paa fakturaen. `plans` siger pr. aftale, hvad der blev taget, og hvorfor det
   * eventuelt blev mindre end oensket.
   */
  buffer: {
    mode: 'auto' | 'manual';
    requested_excl_vat: number;
    applied_excl_vat: number;
    plans: ContractBufferPlan[];
  };
  due_date: string;
  /** Timer i perioden der ikke kan faktureres, med aarsag. Vises som advarsel. */
  unbillable: UnbillableHour[];
  /**
   * Application operations after the hours - on an invoice created on the 1st, for that month.
   * `month` is null on any other day. Already in `totals`. See application-operations.ts.
   */
  application_operations: { month: string | null; lines: ApplicationOperationsLine[] };
  /**
   * The customer's retainer, when it has one - see `CustomerInvoiceRules.retainer`. It replaces
   * the hours: `lines` then carry the hours at 0 kr, so they can still be chosen and marked as
   * invoiced. Already in `totals`.
   */
  retainer: RetainerInvoiceLine | null;
  /** Blokerende problemer. Er listen ikke tom, kan fakturaen ikke oprettes. */
  blockers: string[];
  /** Ikke-blokerende bemaerkninger, fx overskridelse af restbeloeb. */
  warnings: string[];
}

/**
 * Én gruppe i Bilag-previewet paa /fakturering/:id: kontraktlinjen og de opgaver, den daekker.
 *
 * `tasks` er tom, naar opgaverne ikke laengere kan slaas op - se `basisGroupsFromLines` i
 * apps/api/src/demo/invoice-hours-view.ts. Gruppen staar da med sit timetal alene.
 */
export interface InvoiceBasisGroup {
  /**
   * Aftalen gruppen hoerer til, og null for timer uden aftale.
   *
   * **Udeladt** naar bilaget er genskabt af fakturaens egne linjer: da kender Codepass ikke
   * aftalen bag linjen, og `null` ville paastaa at der ikke er nogen. Se `basisGroupsFromLines`.
   */
  contract?: InvoiceLineContract | null;
  contract_line: number | null;
  description: string;
  /**
   * De **arbejdede** timer - summen af `tasks`, og altsaa ikke altid fakturaens antal.
   *
   * Bilaget svarer paa "hvad blev der lavet", fakturaen paa "hvad koster det". En gratis
   * aendring er udfoert arbejde uden et beloeb, saa den taeller her og ikke der. En aendring
   * inden for scope taeller ingen af stederne: den staar med 0 timer. Se `invoiceBasisGroups` i
   * apps/api/src/demo/invoice-hours-view.ts.
   */
  quantity: number;
  unit_price_excl_vat: number;
  /** Fakturaens beloeb for gruppen. Kun de opkraevede timer - se `quantity`. */
  total_excl_vat: number;
  /**
   * Gruppen er en serviceaftales timer.
   *
   * Bilaget viser da **opgaverne** under aftalen - navnet og de brugte timer - frem for kun
   * linjen. En serviceaftale har ingen milepaele at specificere efter, og spoergsmaalet, bilaget
   * skal svare paa, er "hvad blev der lavet": det er opgaverne. Se `renderBilagPdf`.
   */
  service_agreement?: boolean;
  /**
   * Gruppen er aftalens aendringsanmodninger - linjen 'Ændringsanmodninger'.
   *
   * Bilaget viser da **én raekke pr. anmodning**, `CR - <opgavens navn>`, efter milepaelene -
   * frem for linjen med summen. Se `rowsOf` i apps/api/src/domain/bilag-document.ts.
   */
  is_change_request?: boolean;
  /**
   * A fixed fee - the customer's retainer - not hours. The bilag shows it with its quantity under
   * 'Antal' and no hour totals. See `CustomerInvoiceRules.retainer`.
   */
  fixed_fee?: boolean;
  /**
   * `subtasks` er navne uden timetal - se `ContractLineTask.subtasks`.
   *
   * `covered_by_scope` er en aendring inden for det aftalte. Den taeller med 0 timer og uden
   * beloeb, fordi arbejdet var betalt af kontraktsummen, og bilaget skriver `(I scope)` i
   * Timer-kolonnen.
   * Valgfri, saa et genskabt bilag uden opgaver ikke skal paastaa noget om dem.
   *
   * **Der er intet `free` her, og det er ikke en forglemmelse.** Bilaget foelger fakturaen til
   * kunden som vedhaeftet fil, og en gratis aendring siger, at vi har givet arbejdet vaek og
   * trukket det af aftalens buffer - vores forhandlingsposition, ikke kundens oplysning. Flaget
   * findes paa `ContractLineTask` og vises paa /fakturering/ny, hvor controlleren saetter det;
   * det naar bare aldrig denne form. Se `invoiceBasisGroups` i
   * apps/api/src/demo/invoice-hours-view.ts.
   */
  tasks: Array<{
    title: string;
    quantity: number;
    subtasks: string[];
    covered_by_scope?: boolean;
    /**
     * The project agreement a service agreement task was worked on - the 'Kontrakt' column under
     * a service agreement. See `ContractLineTask.project_contract`.
     */
    project_contract?: TaskProjectContract | null;
  }>;
}

/**
 * Svaret fra `GET /invoices/:id/bilag` - bilaget bag Bilag-previewet paa /fakturering/:id.
 *
 * **PDF'en selv og ikke dens indhold.** Previewet viser den fil, kunden faar, frem for en
 * gengivelse af den: to gengivelser af det samme dokument kan drive fra hinanden, og den, der
 * saa er forkert, er den man ikke kan se. Hentet fra Business Centrals vedhaeftede filer, hvor
 * bilaget blev lagt ved oprettelsen.
 *
 * Base64 og ikke en bytestroem: svaret gaar gennem den samme JSON-vej som resten af /invoices -
 * se demo/demo-hook.ts - og en PDF paa nogle kilobyte taber ingenting ved de 33 % overhead.
 */
export interface InvoiceBilagResponse {
  /**
   * Hvor filen kommer fra.
   *
   * `bc` er den vedhaeftede fil i Business Central - det kunden har. `local` er gengivet af
   * Codepass her og nu, fordi BC ikke havde nogen; da maerkes previewet, saa ingen tror, at
   * det, de ser, ligger paa fakturaen.
   */
  source: 'bc' | 'local';
  file_name: string;
  /** PDF'en, base64-kodet. */
  content_base64: string;
  /** Dansk forklaring paa, hvorfor filen ikke kom fra BC. Kun sat naar `source` er `local`. */
  local_reason?: string;
}

/**
 * Svaret fra `GET /invoices/:id/pdf` - fakturaen bag Faktura-previewet paa /fakturering/:id.
 *
 * **BC's egen gengivelse af fakturaen**, samme greb som bilaget: previewet viser det dokument,
 * Business Central sender, frem for en tabel Codepass tegner ved siden af. Paa en kladde er det
 * BC's udkast-layout; naar fakturaen er bogfoert, er det den endelige.
 *
 * Ingen `source`: der er ingen lokal gengivelse af selve fakturaen. Kan BC ikke levere den, svarer
 * ruten med en fejl, og previewet viser fakturaens linjer med en besked om hvorfor.
 */
export interface InvoicePdfResponse {
  file_name: string;
  /** PDF'en, base64-kodet. Se `InvoiceBilagResponse` for hvorfor. */
  content_base64: string;
}

/**
 * Svaret fra `GET /customers/pending-hours?period_start=&period_end=`.
 *
 * Kunderne der har timer markeret til fakturering i perioden - prikken i kundevaelgeren paa
 * /fakturering/ny. Kun id'erne: hvad kunden hedder, staar allerede i listen ved siden af, og
 * svaret skal kunne hentes igen ved hver aendring af perioden uden at koste en kundeliste mere.
 *
 * Et id her lover **timer**, ikke fakturalinjer. Hvad de bliver til - hvilke aftaler de rammer,
 * og hvad der ikke kan faktureres - afgoeres foerst af grundlaget, naar kunden er valgt.
 */
export interface CustomersWithPendingHoursResponse {
  /** Kunde-id'er i Business Central. Uden raekkefoelge; UI slaar op i dem. */
  data: string[];
}

/** Én opgave i tabellen over 'Sales' og 'Not Billable' paa /fakturering/ny. */
export interface UnbilledReviewTask {
  id: string;
  title: string;
  /** Raa OnePro-status - 'Sales' eller 'Not Billable'. Brugeren ser dansk via labels. */
  status: string;
  hours: number;
  /** Datoen opgaven blev meldt faerdig i OnePro, ISO 8601. Perioden afgraenses paa den. */
  completed_date: string;
  consultant_name: string | null;
  /** Aftalen, opgavens tavle hoerer til - stavet som i forespoergslen. */
  contract_no: string;
}

/**
 * Svaret fra `GET /invoices/unbilled-tasks?contract_nos=&period_end=[&period_start=]`. Uden
 * `period_start` begynder perioden ved den foerste opgave.
 *
 * Opgaverne med status 'Sales' eller 'Not Billable' paa de aftaler, fakturaen er ved at blive
 * lavet paa - se `UNBILLED_REVIEW_STATUSES`. Til opslag: de kommer ikke paa fakturaen.
 */
export interface UnbilledReviewTasksResponse {
  data: UnbilledReviewTask[];
  total_hours: number;
  /**
   * Opgaver med en af statusserne paa aftalerne, men uden faerdigdato. Ingen periode kan tage dem
   * med, saa de taelles frem for at mangle uden forklaring.
   */
  without_completion: number;
  /**
   * Faerdigdatoen paa den foerste opgave med statusserne paa aftalerne, uanset perioden. Feltet
   * 'Fra' viser den, naar ingen har valgt en dato - saa tabellen som standard viser alt. Null
   * uden opgaver.
   */
  first_completed_date: string | null;
}

export interface CreateInvoiceRequest extends InvoicePreviewRequest {
  /** Opgaver controlleren har fravalgt. Deres timer forbliver ufakturerede. */
  excluded_task_ids?: string[];
  /**
   * Aendringsanmodninger controlleren har sat til gratis.
   *
   * De faktureres til nul kroner, men taeller som faktureret og kommer aldrig igen; vaerdien
   * traekkes fra aftalens buffer. Kun aendringsanmodninger - serveren ser bort fra et id, der
   * peger paa noget andet. Se `ContractLineTask.free`.
   */
  free_task_ids?: string[];
  /**
   * Et haandskrevet bufferbeloeb i kroner, fordelt paa linjerne inden for opgavernes estimater -
   * se `planInvoiceBuffer`. **Udeladt betyder automatisk**: bufferen regnes af aftalernes samlede
   * buffer. 0 betyder ingen buffer paa denne faktura. Ikke-negativ.
   */
  buffer_excl_vat?: number;
}

/**
 * Aftalens buffer paa /kontrakter/:id - maalet og det, fakturaerne har lagt paa.
 *
 * Maalet staar i `codepass_contract_buffer`; resten er regnet af de fakturaer, Codepass har
 * oprettet - se `contractBufferLedger`.
 */
export interface ContractBufferSummary {
  target_excl_vat: number;
  collected_excl_vat: number;
  /** Gratis arbejde, betalt af bufferen. */
  given_excl_vat: number;
  /** Det, der mangler i at naa maalet. Aldrig negativ. */
  missing_excl_vat: number;
}

/** Body i `PUT /contracts/:id/buffer`. Hele kroner, ikke-negativ. 0 fjerner bufferen. */
export interface UpdateContractBufferRequest {
  buffer_excl_vat: number;
}

/** Udfaldet af seneste automatiske koersel for kunden. */
export interface InvoiceScheduleRun {
  at: string;
  period: InvoicePeriod;
  outcome: 'created' | 'nothing' | 'skipped' | 'failed';
  /** Fakturanummeret i BC, naar en kladde blev oprettet. */
  invoice_no: string | null;
  /** Dansk forklaring, naar intet blev oprettet. */
  message: string | null;
}

/** Kundens faktureringsplan - kortet 'Fakturering' paa /kunder/:id. */
export interface CustomerInvoiceScheduleState {
  schedule: InvoiceSchedule;
  /** Hvem der satte planen. Den automatiske kladde oprettes i deres navn i revisionssporet. */
  set_by_name: string | null;
  set_at: string | null;
  /** Den naeste periode, der oprettes en kladde for, og dagen det sker. Null ved 'manual'. */
  next: { period: InvoicePeriod; run_date: string } | null;
  last_run: InvoiceScheduleRun | null;
  /** Den automatiske fakturering koerer ikke paa denne server - se AUTO_INVOICING. */
  job_enabled: boolean;
}

/** Body i `PUT /customers/:id/invoice-schedule`. */
export interface UpdateInvoiceScheduleRequest {
  schedule: InvoiceSchedule;
}

/** The card 'Fakturaregler' on /kunder/:id - see `CustomerInvoiceRules`. */
export interface CustomerInvoiceRulesState {
  rules: CustomerInvoiceRules;
  set_by_name: string | null;
  set_at: string | null;
}

/** Body of `PUT /customers/:id/invoice-rules`. Every rule at once - the form saves them together. */
export type UpdateCustomerInvoiceRulesRequest = CustomerInvoiceRules;

/** The card 'Timer pr. måned' on /kunder/:id - hours registered in OnePro on the customer's work. */
export interface CustomerMonthlyHoursResponse {
  /** Oldest first, one entry per month, also the months without hours. `YYYY-MM`. */
  months: Array<{ month: string; hours: number; invoiced_hours: number }>;
  total_hours: number;
  /**
   * False when Codepass finds no board or workspace for the customer in OnePro - the zeros then
   * mean "not linked", not "no work".
   */
  linked: boolean;
}

export interface ApproveInvoiceRequest {
  confirmed: boolean;
  note_to_bc?: string;
  send_immediately: boolean;
}

export interface RejectHoursRequest {
  hour_ids: string[];
  reason: string;
}

export interface ApproveHoursRequest {
  hour_ids: string[];
  /** Kraevet naar mindst én time bringer kontrakten over loftet. */
  override_reason?: string;
}

export interface ResolveAlertRequest {
  resolution_note: string;
}

export interface SnoozeAlertRequest {
  snoozed_until: string;
  resolution_note?: string;
}

/**
 * Antal raekker i dashboardets to lister: 'Seneste kontrakter' og 'Alarmer'.
 *
 * Ét tal for begge, fordi de staar side om side i den samme gridraekke med den samme faste
 * hoejde - se Dashboard.tsx. Hver liste skal have nok raekker til at fylde sin halvdel; hvad
 * der ikke er plads til, rulles. Er de to tal forskellige, bliver det ene panel det, man kan
 * se bunden af, og det andet det, man altid ruller i - uden at der er en grund til forskellen.
 *
 * Tallet er ikke en graense paa beholdningen. Alarmtallene i `open_alerts` taeller alle aabne,
 * og /alarmer viser dem alle.
 */
export const DASHBOARD_RECENT_ROWS = 10;

export interface DashboardResponse {
  kpis: {
    active_contracts: number;
    active_contracts_delta: number;
    billed_ytd_excl_vat: number;
    billed_ytd_pct_of_contract_value: number;
    unbilled_wip_excl_vat: number;
    unbilled_wip_hours: number;
    /**
     * Days to pay: from posting to payment, weighted by amount, over the last year's payments -
     * see `daysToPay`, read from BC's application entries (08-10-2026). Estimated when they
     * cannot be read: from the payment terms when the ledger cannot be read either. Se OQ-10.
     */
    dso_days: number | null;
    dso_is_estimated: boolean;
    /** The payments `dso_days` is weighted over. Null when it is not from payments. */
    dso_payment_count: number | null;
  };
  open_alerts: { critical: number; warning: number; info: number };
  recent_contracts: Array<{
    id: string;
    /**
     * Kontraktnummeret, naar aftalen kommer fra Dynamics. Null for en kontrakt fra databasen,
     * som ikke har et. Baeres med, saa linket kan bygges af `contractPath` som paa
     * /kontrakter - ellers ville dashboardet vaere det ene sted der linkede med et GUID.
     */
    contract_no: string | null;
    title: string;
    customer_name: string;
    contract_sum_excl_vat: number;
    billed_excl_vat: number;
    billed_pct: number;
    status: string;
  }>;
  recent_alerts: Array<{
    id: string;
    title: string;
    severity: Severity;
    created_at: string;
    contract_id: string | null;
    /**
     * Whose alarm it is. The row links to the alarm only for someone who may open it - their own,
     * or anyone's with 'Se alle alarmer'. Absent where the source knows no recipient.
     */
    recipient_id?: string | null;
  }>;
  billing_by_month: Array<{
    month: string;
    billed_excl_vat: number;
    /**
     * The month's revenue budget in Business Central - the same as the TV screen's month target,
     * see `TvBudgetYear`. Null when BC has no budget for the year. Replaced the active contract
     * value / 12 on 05-10-2026, which stood in while there was no budget.
     */
    budget_excl_vat: number | null;
  }>;
  /**
   * The five customers who have paid the most this year, by payment date, most first.
   * `paid_excl_vat` comes from BC's application entries - see `paidExclVat`; null where it is not
   * known (the Codepass schema has no payments). `billed_excl_vat` is this year's posted invoices.
   */
  top_customers: Array<{
    customer_id: string;
    name: string;
    billed_excl_vat: number;
    paid_excl_vat: number | null;
  }>;
  detection_last_run_at: string | null;
}

/**
 * The wall screens. Each has its own address and its own token in `codepass_integrations`, so one
 * can be revoked without turning the others off; the IP in `DASHBOARD_TV_IP` is shared. Specs in
 * docs/dashboard: Storskaerm-1-Aendringer-Spec.md, Storskaerm-2-Spec.md and the PDF for screen 3.
 */
export const TV_SCREENS = ['growth', 'projects', 'internal'] as const;
export type TvScreen = (typeof TV_SCREENS)[number];

export const TV_SCREEN_INFO: Record<
  TvScreen,
  {
    /** What /admin/tv-dashboard calls it. */
    label: string;
    /** The screen's address in the web app. The token goes in `?token=`. */
    path: string;
    token_setting: IntegrationSettingName;
  }
> = {
  growth: {
    label: 'Storskærm 1 · vækst',
    path: '/dashboard/tv',
    token_setting: 'DASHBOARD_TV_TOKEN',
  },
  projects: {
    label: 'Storskærm 2 · projekter',
    path: '/dashboard/tv/projekter',
    token_setting: 'DASHBOARD_TV_2_TOKEN',
  },
  internal: {
    label: 'Storskærm 3 · intern udvikling',
    path: '/dashboard/tv/intern',
    token_setting: 'DASHBOARD_TV_3_TOKEN',
  },
};

/** GET /tv-dashboard/screens/:screen/url - the screen's token, for "Kopier URL". */
export interface TvScreenToken {
  screen: TvScreen;
  /** Null when no token is set: the screen is closed, and there is no address to copy. */
  token: string | null;
}

/**
 * Storskaermen opdaterer sig selv saa ofte - B13 i docs/dashboard/1-1-Storskaerm-Dashboard-
 * Designnotat.md. API'et cacher hver kilde for sig, saa et kald oftere ville ikke give nyere tal.
 */
export const TV_DASHBOARD_REFRESH_MS = 5 * 60_000;

/**
 * How often the screen asks whether an admin pressed "Opdater skærmen nu" on /admin/tv-dashboard.
 * The question is one timestamp held in the API's memory, so asking often costs nothing - and it
 * is how long the button can take to reach the screen.
 */
export const TV_DASHBOARD_REFRESH_CHECK_MS = 10_000;

/** GET /dashboard/tv/refresh - when an admin last asked the screen for fresh numbers. */
export interface TvRefreshStatus {
  /**
   * Null when nobody has asked since the API started. The screen fetches anew when this is later
   * than the `generated_at` of the numbers it shows.
   */
  requested_at: string | null;
  /**
   * What every screen shows instead of its numbers right now - the welcome text or a birthday.
   * Null when nothing does. Asked with the refresh, so it reaches the screens within
   * `TV_DASHBOARD_REFRESH_CHECK_MS`, and goes again as quickly.
   */
  override: TvOverride | null;
}

/**
 * On all the wall screens at once: a big text, centered - or a YouTube video over the whole
 * screen, where `url` is its embedded player, from `youtubeEmbedUrl`.
 */
export type TvOverride =
  { kind: 'welcome' | 'birthday'; text: string } | { kind: 'youtube'; url: string };

/** What the welcome screen shows (03-11-2026): the text or a YouTube video. */
export const TV_WELCOME_MODES = ['text', 'youtube'] as const;
export type TvWelcomeMode = (typeof TV_WELCOME_MODES)[number];

/**
 * The welcome screen on /admin/tv-dashboard, stored as `DASHBOARD_TV_WELCOME`. The text or video
 * takes over all three screens while `active` and before `until`. The birthday greeting takes
 * over from `TV_BIRTHDAY_HOUR` for `birthday_minutes` on a day someone has a birthday - and wins
 * over the welcome screen while it does (decided 23-10-2026).
 */
export interface TvWelcomeSettings {
  mode: TvWelcomeMode;
  /** Free text. Line breaks are kept. */
  text: string;
  /** The video's YouTube link, as pasted. Kept while `mode` is 'text'. */
  url: string;
  active: boolean;
  /** ISO timestamp. Null: until someone presses Stop. */
  until: string | null;
  /** 0 turns the greeting off. */
  birthday_minutes: number;
}

export const TV_WELCOME_TEXT_MAX = 500;
export const TV_WELCOME_URL_MAX = 2000;
/** The greeting goes up at this hour, Danish time. */
export const TV_BIRTHDAY_HOUR = 12;
export const TV_BIRTHDAY_MINUTES_DEFAULT = 60;
/** Up to midnight at the most. */
export const TV_BIRTHDAY_MINUTES_MAX = (24 - TV_BIRTHDAY_HOUR) * 60;

/** GET and PUT /tv-dashboard/welcome. */
export interface TvWelcomeAdmin {
  settings: TvWelcomeSettings;
  /** What the screens show now instead of their numbers. Null: their numbers. */
  override: TvOverride | null;
  /** Today's birthdays in OneCrew - only those who have chosen to show theirs. Full names. */
  birthdays_today: string[];
  /** Why OneCrew could not be read, in Danish. Null when it could. */
  error: string | null;
}

/** Har en kilde ikke svaret i saa mange minutter, skifter prikken ved "Opdateret" til gul. */
export const TV_DASHBOARD_STALE_MINUTES = 30;

/**
 * Maalstregen for faktureringsgrad, naar /admin/tv-dashboard ikke har en anden. Foreslaaet af
 * designnotatet - O5. Hver medarbejder kan have sit eget - se `TvUtilizationSettings`.
 */
export const TV_UTILIZATION_TARGET_PCT = 70;
export const TV_UTILIZATION_TARGET_MIN_PCT = 1;
export const TV_UTILIZATION_TARGET_MAX_PCT = 100;

/** Ugentlig normtid i timer, naar /admin/tv-dashboard ikke har en anden. En fuldtidsuge. */
export const TV_WEEKLY_HOURS_DEFAULT = 37;
export const TV_WEEKLY_HOURS_MIN = 1;
export const TV_WEEKLY_HOURS_MAX = 60;

/**
 * The screen's zoom in percent, set on /admin/tv-dashboard. It scales the whole picture around its
 * center, like zooming an image; the layout itself does not change. 100 fills the screen. Below
 * that the picture shrinks and gets a border in the page color - the fix for a TV that crops or
 * crowds the edges. Above it the picture grows and its edges are cut off.
 *
 * Steps of 1, because fitting the edges of a particular TV typically lands on 92 or 96 rather
 * than a round number.
 */
export const TV_ZOOM_DEFAULT_PCT = 100;
export const TV_ZOOM_MIN_PCT = 50;
export const TV_ZOOM_MAX_PCT = 150;
export const TV_ZOOM_STEP_PCT = 1;

/**
 * Autoscroll on "Faktureringsgrad pr. person", set on /admin/tv-dashboard. When there are more
 * people than rows that fit, the list scrolls slowly downwards and loops without end. 'off' keeps
 * the old behaviour: the rows share the height, however tight that gets.
 */
export const TV_AUTOSCROLL_OPTIONS = ['off', 'slow', 'normal', 'fast'] as const;
export type TvAutoscroll = (typeof TV_AUTOSCROLL_OPTIONS)[number];
export const TV_AUTOSCROLL_DEFAULT: TvAutoscroll = 'normal';

/**
 * Speed in pixels per second on the 1920 x 1080 stage. At 'normal' a person stays in view for
 * about 20 seconds.
 */
export const TV_AUTOSCROLL_PX_PER_SECOND: Record<Exclude<TvAutoscroll, 'off'>, number> = {
  slow: 12,
  normal: 20,
  fast: 32,
};

/** GET and PUT /tv-dashboard/settings. PUT takes any of the fields and leaves the rest. */
export interface TvDashboardSettings {
  zoom_pct: number;
  autoscroll: TvAutoscroll;
}

/**
 * A year's revenue budget: the entries on income accounts, summed per month. The source of the
 * screen's goals - see demo/tv-goals.ts.
 */
export interface TvBudgetYear {
  year: number;
  /** The budget's name in BC, e.g. `BUDGET2026`. `Codepass` when BC has none for the year. */
  budget_name: string;
  /**
   * Where each month comes from, January first: /budget when the month has revenue there, BC's
   * budget otherwise (decided 26-10-2026). See `budgetSourceText`.
   */
  month_sources: BudgetSource[];
  /** The twelve months together. */
  target_excl_vat: number;
  /** Twelve months, January to December. A month without entries in BC is 0. */
  months: number[];
  /** The income accounts the entries are on: number and name, e.g. "1010 Salg af ydelser". */
  accounts: string[];
  /** Accounts in the same budget that are not income - costs, say - and so do not count. */
  skipped_accounts: string[];
}

/** A year BC has budget entries for that the screen cannot use, and why - in Danish. */
export interface TvBudgetProblem {
  year: number;
  message: string;
}

/** GET /budget?year= - the year's budget in the chart of accounts, as /budget shows it. */
export interface BudgetResponse {
  year: number;
  /** False until the table in docs/budget-opsaetning.md exists. Then nothing can be saved. */
  ready: boolean;
  /** The income statement in the chart of accounts, in BC's order. */
  rows: BudgetSheetRow[];
  /** What the TV screens and /dashboard measure the year against. Null: no target. */
  target: TvBudgetYear | null;
  /** Why the year has no target, in Danish. */
  problem: string | null;
}

/**
 * PUT /budget/entries. A `month` (1-12) gets the whole amount; null spreads it over the twelve.
 * Either way it replaces what the months held.
 */
export interface BudgetEntryInput {
  year: number;
  account_no: string;
  amount: number;
  month: number | null;
}

/** POST /budget/import - the Excel file from GET /budget/export, filled in. */
export interface BudgetImportInput {
  year: number;
  file_base64: string;
}

/*
 * `TvGoalSettings` and `TV_BACKLOG_HOURLY_RATE_DEFAULT` are gone (09-10-2026): the backlog's
 * hourly rate was the only goal set in Codepass, and the backlog went off the screen. The goals
 * are the budget in Business Central - see `TvBudgetResponse`.
 */

/**
 * Faktureringsgradens indstillinger: normtid og maal, faelles og pr. medarbejder. Redigeres paa
 * /admin/tv-dashboard og gemmes som `DASHBOARD_TV_UTILIZATION` - body'en i PUT
 * /tv-dashboard/utilization.
 *
 * The people are the users on /admin/brugere. A field that is null follows the shared value, so a
 * change to the default reaches everyone who has no value of their own.
 */
export interface TvUtilizationSettings {
  default_weekly_hours: number;
  default_target_pct: number;
  people: TvUtilizationPersonSettings[];
}

export interface TvUtilizationPersonSettings {
  /** The user's ID in `users` - the same on /admin/brugere and in OnePro's timers. */
  user_id: string;
  /** Ugentlig normtid i timer. Null = standarden. */
  weekly_hours: number | null;
  /** Maal i procent. Null = standarden. */
  target_pct: number | null;
  /** Staar ikke paa skaermen og taeller ikke i husets tal - fx en medarbejder uden kundearbejde. */
  hidden: boolean;
}

/** En medarbejder paa /admin/tv-dashboard: indstillingerne og maanedens tal til dato. */
export interface TvUtilizationAdminPerson extends TvUtilizationPersonSettings {
  name: string;
  email: string;
  /** Active on /admin/brugere. A deactivated user is not on the screen. */
  active: boolean;
  /** Has registered time in OnePro in the last half year. Without it, the user is not on the screen. */
  has_recent_time: boolean;
  weekly_hours_effective: number;
  target_pct_effective: number;
  /** Vacation and illness from the absence calendar in Outlook. Not on a day already off. */
  absence_days: number;
  /** Days off, school and courses from the calendar on /admin/tv-dashboard - see `TvCalendarEntry`. */
  off_days: number;
  available_hours: number;
  billable_hours: number;
  pct: number | null;
  /** Time on internal tasks in OnePro. Never in `pct`. */
  internal_hours: number;
  /**
   * All work, billable and internal, over the hours available. Today counts with all the work
   * registered on it so far, where `pct` counts only the billable.
   */
  total_pct: number | null;
  /** On the screen: not hidden, active, with recent time, and with hours available. */
  on_screen: boolean;
}

/**
 * What a day in the calendar on /admin/tv-dashboard is: a day off for everyone, school, a course or
 * parental leave. Only the label and colour differ - every kind is a day off the same way.
 */
export const TV_CALENDAR_KINDS = ['holiday', 'school', 'course', 'parental_leave'] as const;
export type TvCalendarKind = (typeof TV_CALENDAR_KINDS)[number];

/**
 * A stretch of days that are not working days for the people it names, though they are not
 * absent either: days off OneCode gives on top of the legal holidays, school for a trainee, a
 * course. Edited in the calendar on /admin/tv-dashboard and stored as `DASHBOARD_TV_CALENDAR`.
 * Vacation and illness are not here - they come from the absence calendar in Outlook.
 */
export interface TvCalendarEntry {
  id: string;
  kind: TvCalendarKind;
  /** The users on /admin/brugere it applies to. Null = everyone. */
  user_ids: string[] | null;
  /** First and last day, ISO. Both included. */
  start_date: string;
  end_date: string;
  /** ISO weekdays, Monday = 1 to Friday = 5. Empty = every working day from first to last. */
  weekdays: number[];
  note: string | null;
}

/** The body of POST and PUT /tv-dashboard/calendar: an entry without its ID. */
export type TvCalendarEntryInput = Omit<TvCalendarEntry, 'id'>;

/** GET /tv-dashboard/calendar. */
export interface TvCalendarResponse {
  /** By first day. */
  entries: TvCalendarEntry[];
  /** The users on /admin/brugere, to choose from - by name. */
  users: Array<{ id: string; name: string; active: boolean }>;
}

/** An event in the absence calendar that names no one on /admin/brugere, so it counts for no one. */
export interface TvUnmatchedAbsence {
  subject: string | null;
  /** First and last day, Danish dates. */
  start_date: string;
  end_date: string;
}

/** GET og PUT /tv-dashboard/utilization. */
export interface TvUtilizationAdmin {
  settings: Omit<TvUtilizationSettings, 'people'>;
  /**
   * The absence calendar in Outlook, and Entra ID, through which a full name in the calendar
   * becomes a user on /admin/brugere.
   */
  source: {
    /** The Entra app's tenant, client ID and secret are set - the same as Dynamics uses. */
    configured: boolean;
    /** The absence calendar, `fravaer@onecode.dk`. */
    mailbox: string;
    /** Why the users, Entra ID or the absence could not be read. Null when all could. */
    error: string | null;
    /** When the absence was last read. Null when it has not been read since start-up. */
    fetched_at: string | null;
    /**
     * When the absence calendar itself last changed - the newest new or changed event. Null when
     * it is empty or could not be read. The rule `fravaerskalender` alarms when it gets old.
     */
    calendar_updated_at: string | null;
    unmatched_absences: TvUnmatchedAbsence[];
  };
  /** Maaneden til og med i dag - samme periode som skaermen. */
  period: {
    start: string;
    through: string;
    working_days: number;
    /** Helligdagene paa en hverdag i perioden. */
    holidays: Array<{ date: string; name: string }>;
  };
  people: TvUtilizationAdminPerson[];
  house: {
    pct: number | null;
    target_pct: number;
    billable_hours: number;
    available_hours: number;
    internal_hours: number;
    /** All work over the hours available - see `TvUtilizationAdminPerson.total_pct`. */
    total_pct: number | null;
  };
}

/**
 * Faktureret i én periode, holdt op mod periodens budget i Business Central.
 *
 * Faktureret er de bogfoerte fakturaer i BC med fakturadato i perioden, ekskl. moms - samme regel
 * som "Faktureret i aar" paa /dashboard (`countsAsBilled`). Kladder taeller ikke.
 */
export interface TvBilledPeriod {
  /** Periodens foerste og sidste dag (ISO). */
  start: string;
  end: string;
  billed_excl_vat: number;
  /** The budget in Business Central for the period. Null when BC has none - see `TvBudgetYear`. */
  target_excl_vat: number | null;
  /** Faktureret i procent af maalet. Null uden maal. */
  pct_of_target: number | null;
}

/**
 * Hvor aaret staar mod den lineaere forventning.
 *
 * `ahead` er paa eller over forventningen, `slightly_behind` er under med mindre end
 * `TV_PACE_SLACK_PCT` procentpoint, `behind` er resten. Null uden et aarsmaal.
 */
export type TvPace = 'ahead' | 'slightly_behind' | 'behind';

/** Hvor mange procentpoint under forventningen, der stadig kun er "lidt bagud". */
export const TV_PACE_SLACK_PCT = 5;

/**
 * Storskaermen paa /dashboard/tv - OneCodes faelles vaekstmaal paa én statisk skaerm. Specen er
 * docs/dashboard/1-1-Storskaerm-Dashboard-Designnotat.md, layoutet er mockuppen ved siden af.
 *
 * Ingen handlinger og ingen bruger: adgangen er et token og en IP, se apps/api/src/lib/tv-access.ts.
 * Ingen timepriser og ingen enkeltprojekter - B29. Salg pr. saelger staar der siden 22-10-2026
 * (afloeser B4) - `sales_per_seller`. Top kunder flyttede til storskaerm 2 samme dag, og det interne
 * arbejde til storskaerm 3.
 *
 * **Kun det, skaermen viser** (Storskaerm-1-Aendringer-Spec.md, acceptkriterierne): svaret baerer
 * ingen felter ud over dem, der tegnes.
 *
 * **Alle tal er regnet i API'et.** Browseren formaterer og tegner. Et tal, der ikke kan regnes,
 * fordi kilden mangler, er null og ikke 0: et nul paa en vaegskaerm bliver laest som en maaling.
 */
export interface TvDashboardResponse {
  generated_at: string;
  /**
   * Hvor gamle tallene er. `generated_at`, naar alle kilder svarede ved seneste forsoeg, ellers
   * seneste svar fra den aeldste kilde, der ikke svarede.
   */
  updated_at: string;
  /**
   * Mindst én kilde har ikke svaret i `TV_DASHBOARD_STALE_MINUTES` - eller aldrig. Skaermen viser
   * da de seneste tal, den har, og skifter prikken til gul. B18: aldrig en tom skaerm.
   */
  stale: boolean;
  /** How the screen is drawn. Sent with the numbers, so a change reaches the screen on its next refresh. */
  display: TvDashboardSettings;
  month: TvBilledPeriod & {
    /** Forrige maaned, til "august: 108 % af maal". */
    previous: TvBilledPeriod;
  };
  quarter: TvBilledPeriod & {
    /** 1-4. */
    quarter: number;
    /** Dage tilbage af kvartalet, i dag medregnet. */
    days_left: number;
    /**
     * Den hvide streg paa bjaelken: hvor langt budgettet forventer kvartalet i dag, i procent.
     * Foelger maanedsmaalene; uden dem den andel af kvartalet, der er gaaet foer i dag.
     */
    expected_pct: number;
    /** Maalet minus det fakturerede. Null uden maal; negativt, naar maalet er naaet. */
    remaining_to_target_excl_vat: number | null;
    /** Ufaktureret arbejde - samme tal som WIP-kortet paa /dashboard. */
    wip_excl_vat: number;
  };
  year: TvBilledPeriod & {
    year: number;
    days_left: number;
    /** Som kvartalets: budgettets forventning i dag, eller den lineaere uden maanedsmaal. */
    expected_pct: number;
    pace: TvPace | null;
  };
  /**
   * Utilization: billable hours divided by available hours (weekly norm x working days - absence -
   * holidays). This month up to and including today; the previous month whole. Null when the
   * people or the absence cannot be read, or no one is linked to OnePro - see `missing_reason`.
   */
  utilization: {
    /**
     * Husets maal: de personlige maal vaegtet med de tilgaengelige timer. Uden personlige maal er
     * det standardmaalet - se `weightedTargetPct`.
     */
    target_pct: number;
    house_pct: number | null;
    previous_house_pct: number | null;
    /*
     * `house_total_pct` and each person's `total_pct` - the internal work beside the billable - are
     * gone from the screen (22-10-2026). Internal work is storskaerm 3's: see `TvInternalResponse`.
     */
    /**
     * Den, der er laengst over sit eget maal, foerst (23-10-2026) - ikke den hoejeste grad. Tom,
     * naar tallene mangler. Hver med sit eget maal.
     */
    people: Array<{ name: string; pct: number; target_pct: number }>;
    /** Hvorfor tallene mangler, til skaermen. Null, naar de er der. */
    missing_reason: string | null;
  };
  /** Salg lukket som vundet i aar - `actualvalue` paa opportunity. */
  order_intake: {
    amount_excl_vat: number;
    won_count: number;
    /** Kunder, hvis foerste bogfoerte faktura ligger i aar - F6. */
    new_customers: number;
  };
  /** Aabne salg i CRM. Vaegtet med `closeprobability`; et tomt felt vaegter 0 - F4. */
  pipeline: {
    weighted_excl_vat: number;
    unweighted_excl_vat: number;
    open_count: number;
    /** Aabne salg med en sandsynlighed. Er det 0, er det vaegtede tal 0 af den grund. */
    with_probability_count: number;
  };
  /** Solgt, ikke udfoert arbejde paa de indgaaede aftaler - F7. */
  order_book: {
    hours: number;
    /** Timerne i arbejdsdage. Null, indtil normtiden findes. */
    days: number | null;
    contract_count: number;
    change_request_count: number;
  };
  /*
   * `backlog` - the year's distance to the budget's line in hours - was removed 09-10-2026. The
   * year card says how it is doing against the line with `year.pace` again, as before 01-10-2026.
   * `win_rate` gave its box to `ready_to_invoice` 22-10-2026.
   */
  /**
   * "Salg pr. sælger · Q4 2026": each seller's goal in CRM (Performance -> Goals) for the current
   * quarter - budget, won and in progress, exactly as CRM's rollup has them. Codepass computes none
   * of it. Only the sellers marked on /admin/tv-dashboard, at most `TV_SELLER_ROWS`.
   */
  sales_per_seller: {
    /** 1-4, and the year it is in. */
    quarter: number;
    year: number;
    /** In the order chosen on /admin/tv-dashboard. Empty when CRM cannot be read. */
    sellers: TvSeller[];
    /** CRM's last rollup of the goals shown - the newest `lastrolledupdate`. Null without goals. */
    rolled_up_at: string | null;
    /** Why there is nobody to show, in Danish. Null when there is. */
    missing_reason: string | null;
  };
  /**
   * "Klar til fakturering": what could be invoiced today. The hours OnePro has marked ready that
   * are on no invoice, at the contract's rate, and the closed milestones on project contracts that
   * are not invoiced, at their estimate times the rate. Decided 23-10-2026 - see
   * tv-ready-to-invoice.ts in the API.
   */
  ready_to_invoice: {
    amount_excl_vat: number;
    milestone_count: number;
    /** Hours on the ready tasks outside the counted milestones. */
    hours: number;
    contract_count: number;
    /** Days since the oldest item became ready. Null when nothing is. */
    oldest_days: number | null;
  };
  /**
   * "Løbende indtjening · faste ydelser": what the signed agreements bring in every month from
   * operations, hosting and licences - the lines of those products on the agreements in CRM - plus
   * the subscriptions from /fakturering/abonnementer running this month. See `recurringRevenueOf`
   * in the API. Reverses B25 (MRR removed), decided 01-10-2026.
   */
  recurring_revenue: {
    monthly_excl_vat: number;
    /** The month times twelve. */
    annual_excl_vat: number;
    /** Agreements with at least one such line, or a running subscription bound to them. */
    contract_count: number;
  };
}

/** Rows in "Salg pr. sælger". The box has a fixed layout: fewer sellers leave empty rows. */
export const TV_SELLER_ROWS = 3;

/** A seller in "Salg pr. sælger" - their first name and the goal's figures, nothing else. */
export interface TvSeller {
  name: string;
  /**
   * The goal's `targetmoney`, `actualmoney` and `inprogressmoney`, and won over budget in whole
   * percent. All four null when CRM has more than one active goal for the seller in the quarter:
   * Codepass does not pick one, the screen shows "—", and the rule `salgsmaal` raises an alarm.
   */
  budget_excl_vat: number | null;
  won_excl_vat: number | null;
  in_progress_excl_vat: number | null;
  pct: number | null;
  /**
   * Budget minus won, never below 0: 0 once the goal is reached, and the screen says "Mål nået"
   * (Storskaerm-1-Maalvisning.md). Null with the figures above.
   */
  remaining_excl_vat: number | null;
}

/**
 * "Top kunder": each customer's share of the hours we are invoicing this month, over all their
 * projects - the tasks on an invoice in OnePro (`invoice_pending` with an `invoice_pending_id`),
 * finished this month, Not Billable left out. The customer is the board's workspace in OnePro.
 * Decided 15-10-2026 - see customer-invoice-hours.ts in the API. On storskaerm 2 since 22-10-2026.
 */
export interface TvTopCustomers {
  /** The month's first day, ISO. */
  month_start: string;
  /** Every customer's hours in the month - what the shares are of. */
  total_hours: number;
  /** Customers with hours in the month, the ones shown and the rest. */
  customer_count: number;
  /**
   * Most first, at most `TV_TOP_CUSTOMER_ROWS`. `pct` is the share of `total_hours`, one decimal.
   * Empty when there are none, or OnePro did not answer.
   */
  customers: Array<{ name: string; hours: number; pct: number }>;
}

export const TV_TOP_CUSTOMER_ROWS = 5;

/** What storskaerm 2 and 3 have in common with screen 1: freshness, and the zoom. */
interface TvScreenFrame {
  generated_at: string;
  /** As on `TvDashboardResponse`: the oldest source that did not answer, else `generated_at`. */
  updated_at: string;
  stale: boolean;
  /** The zoom is shared by the screens - set on /admin/tv-dashboard. */
  display: TvDashboardSettings;
}

/** Projects per page on storskaerm 2. More turn the page every `TV_PROJECT_PAGE_SECONDS`. */
export const TV_PROJECT_ROWS = 10;
export const TV_PROJECT_PAGE_SECONDS = 20;

/**
 * Storskaerm 2 - "Hvor står vores projekter?" Docs/dashboard/Storskaerm-2-Spec.md. Every figure
 * is one Codepass already shows: the Burndown tab, the Milepæle and Tillægsaftaler tabs, and
 * Rapporter -> Kapacitet. No customer names in the rows, no amounts, nothing per employee.
 */
export interface TvProjectsResponse extends TvScreenFrame {
  /** Every active project - also those on the second page - by status, then expected finish. */
  projects: TvProject[];
  capacity: {
    /** Free hours a new project needs in one week - one week of the shared norm. */
    project_hours: number;
    /** The first week with room, as the card "Plads til et nyt projekt". Null: none in the weeks. */
    ready_week: { year: number; week: number; free_hours: number } | null;
    booked_pct_4_weeks: number | null;
    free_hours_4_weeks: number;
    /** Eight weeks from this one: the team's free hours after everything, and the unassigned taken. */
    weeks: Array<{ year: number; week: number; team_free: number; unassigned: number }>;
    unassigned: {
      hours: number;
      tasks: number;
      /** Most hours first, at most four. */
      boards: Array<{ name: string; tasks: number; hours: number }>;
    };
  };
  top_customers: TvTopCustomers;
}

export interface TvProject {
  /** `ONC-1006`. Several when the board has several contracts. */
  contract_no: string;
  /** The contract's title. Never the customer's name. */
  name: string;
  /** Assignees on the board's open tasks, most open estimate first. First names. */
  developers: string[];
  /** One per milestone in the board's order, General left out. */
  milestones: Array<'closed' | 'open'>;
  /** (cap - hours left) / cap, whole percent. Null without a cap. */
  progress_pct: number | null;
  /** "Foran/bagud planen" in hours. Positive is ahead. Null without a plan. */
  plan_deviation_hours: number | null;
  pace_hours_per_week: number;
  expected_finish: string | null;
  /** Deadline minus expected finish, calendar days. Null without either. */
  margin_days: number | null;
  open_change_requests: number;
  status: ProjectStatus;
  status_reason: string;
}

/** Projects shown at once on storskaerm 3 - the PDF has room for seven. */
export const TV_INTERNAL_ROWS = 7;

/**
 * Storskaerm 3 - "Hvad bygger vi til os selv?" The PDF in docs/dashboard is the design. Its source
 * (decided 23-10-2026): the boards in OnePro's internal workspaces that no contract is linked to,
 * and the time on them. The roadmap is Orchestrator's `internal_projects` (27-10-2026). There is no
 * source yet for the system per board or a roadmap project's start, so they are not here.
 */
export interface TvInternalResponse extends TvScreenFrame {
  /** Boards with open tasks, by status, then expected finish. */
  projects: TvInternalProject[];
  /** Internal projects not rejected and not yet pushed to OnePro - approved first, then by number. */
  roadmap: TvInternalRoadmapItem[];
  /** This month: internal hours and their share of all registered hours. */
  month: {
    month_start: string;
    internal_hours: number;
    share_pct: number | null;
    previous_share_pct: number | null;
    year_hours: number;
  };
  /** The last six months, oldest first, this month last. */
  months: Array<{ month_start: string; internal_hours: number }>;
  /** The monthly budget line - `DASHBOARD_TV_INTERNAL_BUDGET`. */
  budget_hours: number;
  /** Internal hours per board this year, most first, at most five. */
  by_project: Array<{ name: string; hours: number }>;
  /** Tasks finished on the internal boards in the last 30 days, newest first, at most five. */
  completed: Array<{ name: string; date: string }>;
}

export interface TvInternalProject {
  name: string;
  /** The OnePro workspace the board is in. */
  workspace: string | null;
  /** Everyone with time on the board, most hours first, then open-task assignees. First names. */
  developers: string[];
  used_hours: number;
  /** The tasks' estimates summed, follow-up chains once. Null without any. */
  estimate_hours: number | null;
  used_pct: number | null;
  expected_finish: string | null;
  margin_days: number | null;
  status: ProjectStatus;
  status_reason: string;
}

export interface TvInternalRoadmapItem {
  /** Orchestrator's `project_number`. */
  project_number: number;
  title: string;
  /** `system_name`, or "Nyt projekt" without one. */
  system: string;
  estimate_hours: number;
  /** `approved` in Orchestrator; otherwise awaiting approval. */
  approved: boolean;
}

/** GET and PUT /tv-dashboard/sellers - who is on "Salg pr. sælger", and which goal metric. */
export interface TvSellerSettings {
  /** CRM's goal metric ("OneCode Salg") by id, not name - the spec's rule. Null until chosen. */
  metric_id: string | null;
  /** The users on /admin/brugere marked "Vis", in screen order. At most `TV_SELLER_ROWS`. */
  user_ids: string[];
}

export interface TvSellersAdmin {
  settings: TvSellerSettings;
  /** The goal metrics in CRM, to choose from. Empty when they cannot be read - see `error`. */
  metrics: Array<{ id: string; name: string }>;
  /** The users, with the goal found for each this quarter. */
  users: Array<{
    id: string;
    name: string;
    email: string;
    active: boolean;
    shown: boolean;
    /** How many active goals with a budget CRM has for them this quarter. 1 is right. */
    goal_count: number;
  }>;
  /** Why CRM could not be read, in Danish. Null when it could. */
  error: string | null;
}
